import React, { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Gem, ChevronDown, X as XIcon, Search } from 'lucide-react';

// Human-readable label for a raw-material "kind".
const KIND_LABEL = {
  gold: 'Gold',
  diamond: 'Diamond',
  color_stone: 'Colour Stone',
  pearl: 'Pearl',
};

// Small colour dot per material kind so the option list is quick to scan.
const KIND_DOT = {
  gold: 'bg-amber-500',
  diamond: 'bg-sky-500',
  color_stone: 'bg-rose-500',
  pearl: 'bg-slate-400',
};

/**
 * RawMaterialFilter — multi-select popover for filtering by Raw Material
 * (a specific packet / gold purity currently with the goldsmith).
 *
 * Mirrors the CustomerFilter / ReferenceFilter pattern. Each option carries a
 * composite ``key`` (``${kind}:${id}``) so callers can match both the material
 * kind and its identifier. The caller owns ``value`` (array of keys) and
 * ``onChange``.
 *
 * Props:
 *   options       {key, label, kind}[]   distinct raw materials to choose from
 *   value         string[]      currently selected composite keys
 *   onChange      (next: string[]) => void
 *   counts        Map<key, number>  optional per-material match counts
 *   testIdPrefix  string        data-testid namespace
 */
export function RawMaterialFilter({
  options = [],
  value = [],
  onChange,
  counts,
  testIdPrefix = 'raw-material-filter',
}) {
  const [q, setQ] = useState('');
  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const KIND_RANK = { gold: 0, diamond: 1, color_stone: 2, pearl: 3 };
  const sortedOptions = useMemo(() => {
    const seen = new Map();
    for (const o of (options || [])) {
      if (o && o.key && !seen.has(o.key)) {
        seen.set(o.key, { label: o.label || 'Material', kind: o.kind || '' });
      }
    }
    return Array.from(seen.entries())
      .map(([key, meta]) => ({ key, ...meta }))
      .sort((a, b) => {
        const ra = KIND_RANK[a.kind] ?? 99;
        const rb = KIND_RANK[b.kind] ?? 99;
        if (ra !== rb) return ra - rb;
        return String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [options]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return sortedOptions;
    return sortedOptions.filter(
      (o) => String(o.label).toLowerCase().includes(term) || (KIND_LABEL[o.kind] || '').toLowerCase().includes(term),
    );
  }, [sortedOptions, q]);

  const toggle = (key) => {
    const cur = new Set(value || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    onChange(Array.from(cur));
  };

  const active = (value || []).length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-[12.5px] transition-colors ${
            active
              ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
              : 'border-[var(--divider)] bg-white text-slate-700 hover:bg-slate-50'
          }`}
          data-testid={`${testIdPrefix}-trigger`}
          title="Filter by Raw Material"
        >
          <Gem size={13} />
          <span>{active ? `Material · ${(value || []).length} selected` : 'All materials'}</span>
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start" data-testid={`${testIdPrefix}-content`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Raw Material</div>
          {active && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline"
              data-testid={`${testIdPrefix}-clear`}
            >
              Clear
            </button>
          )}
        </div>
        {sortedOptions.length > 8 && (
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search materials…"
              className="h-8 pl-7 text-[12.5px]"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
        )}
        <div className="max-h-60 overflow-y-auto grid grid-cols-1 gap-1">
          {filtered.length === 0 && (
            <div className="text-[11px] text-slate-500 py-2 text-center">No materials.</div>
          )}
          {filtered.map((o) => {
            const isOn = selectedSet.has(o.key);
            const count = counts ? (counts.get(o.key) || 0) : null;
            return (
              <label
                key={o.key}
                className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 cursor-pointer border ${
                  isOn ? 'bg-amber-50 border-amber-200' : 'border-transparent hover:bg-slate-50'
                }`}
                data-testid={`${testIdPrefix}-option-${o.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox
                    checked={isOn}
                    onCheckedChange={() => toggle(o.key)}
                    data-testid={`${testIdPrefix}-option-${o.key}-checkbox`}
                  />
                  <span className={`w-2 h-2 rounded-full shrink-0 ${KIND_DOT[o.kind] || 'bg-slate-300'}`} />
                  <span className="min-w-0">
                    <span className="text-sm font-mono text-slate-800 truncate block">{o.label}</span>
                    <span className="block text-[11px] text-slate-500">{KIND_LABEL[o.kind] || o.kind}</span>
                  </span>
                </div>
                {count != null && (
                  <span className="text-[11px] font-mono tabular-nums text-slate-500 shrink-0">{count}</span>
                )}
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * RawMaterialFilterChips — removable chips for the selected raw materials.
 */
export function RawMaterialFilterChips({ value = [], labelByKey = {}, onChange, testIdPrefix = 'raw-material-filter-chip' }) {
  if (!Array.isArray(value) || value.length === 0) return null;
  const remove = (key) => onChange((value || []).filter((x) => x !== key));
  return (
    <>
      {value.map((key) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium bg-amber-50 text-amber-800 border-amber-200"
          data-testid={`${testIdPrefix}-${key}`}
        >
          <span className="text-[10px] uppercase tracking-wide opacity-70">Material</span>
          <span className="font-mono truncate max-w-[140px]">{labelByKey[key] || 'Material'}</span>
          <button
            type="button"
            onClick={() => remove(key)}
            className="ml-0.5 inline-flex items-center rounded-full hover:bg-black/10 p-0.5 transition-colors"
            aria-label="Remove raw material filter"
            data-testid={`${testIdPrefix}-${key}-remove`}
          >
            <XIcon size={11} />
          </button>
        </span>
      ))}
    </>
  );
}
